/**
 * Handles Hover-to-Play logic for archive showcase items (Desktop)
 * AND Auto-Play on Scroll for mobile devices.
 */
export const initArchiveShowcaseVideos = (): void => {
  const items = document.querySelectorAll<HTMLElement>("[data-video-hover]");

  if (items.length === 0) return;

  // Check if we are on a touch device or small screen
  // Using 1024px as a safe cutoff for tablets/mobile where hover is unreliable
  const isTouchDevice = window.matchMedia("(max-width: 1024px)").matches;

  // Shared Video Logic
  const startVideo = (
    item: HTMLElement,
    container: HTMLElement,
    template: HTMLTemplateElement
  ) => {
    // Avoid re-injecting if already playing
    if (item.classList.contains("is-playing")) return;

    item.classList.add("is-playing");

    const clone = template.content.cloneNode(true) as DocumentFragment;
    const iframe = clone.querySelector("iframe");

    if (iframe) {
      let src = iframe.getAttribute("src") || "";

      // Extract video ID for playlist looping
      let videoId = "";
      const match = src.match(/\/embed\/([^?]+)/);
      if (match && match[1]) {
        videoId = match[1];
      }

      // Params optimized for background-like playback
      const params = `autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1&loop=1&playlist=${videoId}`;
      const separator = src.includes("?") ? "&" : "?";
      iframe.setAttribute("src", src + separator + params);

      // Ensure pointer events are none so clicking goes to the post link
      iframe.style.pointerEvents = "none";
    }

    container.innerHTML = "";
    container.appendChild(clone);
  };

  const stopVideo = (item: HTMLElement, container: HTMLElement) => {
    if (!item.classList.contains("is-playing")) return;
    item.classList.remove("is-playing");
    container.innerHTML = ""; // Removes iframe, stopping playback immediately
  };

  // --- MOBILE LOGIC (Intersection Observer) ---
  if (isTouchDevice) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const item = entry.target as HTMLElement;
          const container = item.querySelector<HTMLElement>(
            ".archive-showcase-item__video-container"
          );
          const template = item.querySelector<HTMLTemplateElement>(
            ".archive-showcase-item__video-template"
          );

          if (!container || !template) return;

          if (entry.isIntersecting) {
            // Play when element is mostly in view (50%)
            startVideo(item, container, template);
          } else {
            // Stop when element leaves view
            stopVideo(item, container);
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the item is visible
      }
    );

    items.forEach((item) => observer.observe(item));
  }
  // --- DESKTOP LOGIC (Hover) ---
  else {
    items.forEach((item) => {
      const container = item.querySelector<HTMLElement>(
        ".archive-showcase-item__video-container"
      );
      const template = item.querySelector<HTMLTemplateElement>(
        ".archive-showcase-item__video-template"
      );

      if (!container || !template) return;

      let hoverTimeout: number | undefined;

      item.addEventListener("mouseenter", () => {
        // Small delay to prevent flickering on accidental mouseovers
        hoverTimeout = window.setTimeout(
          () => startVideo(item, container, template),
          100
        );
      });

      item.addEventListener("mouseleave", () => {
        if (hoverTimeout) clearTimeout(hoverTimeout);
        stopVideo(item, container);
      });
    });
  }

  console.log(
    `[ArchiveShowcase] Initialized video logic for ${items.length} items. Mode: ${
      isTouchDevice ? "Scroll (Mobile)" : "Hover (Desktop)"
    }`
  );
};
